import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Callout,
  Checkbox,
  Combobox,
  DataListItem,
  Dialog,
  Illustration,
  Input2FA,
} from '@node-space/ui-components'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { MappedErrorMessageState } from 'types/errors'
import { useRewardsDisburseForm } from '../../hooks/useRewardsDisburseForm'
import { useRewardsWalletsOptions } from '../../hooks/useRewardsWalletsOptions'

interface RewardsEmbeddedDisburseDialogProps {
  open: boolean
  onClose: () => void
  onDisburse: (destinationWalletId: string, authenticationCode: string) => void
  otpError?: string
  myRewardsPayout: string
  currency: string
  isSubmitting?: boolean
  requestError?: MappedErrorMessageState
  onClearRequestError?: () => void
}

export const RewardsEmbeddedDisburseDialog = ({
  open,
  onClose,
  onDisburse,
  otpError,
  myRewardsPayout,
  currency,
  isSubmitting = false,
  requestError,
  onClearRequestError,
}: RewardsEmbeddedDisburseDialogProps) => {
  const { t } = useTranslation()

  const {
    destinationWalletId,
    confirmationOfCustomerDistribution,
    confirmationOfIrreversible,
    handleDestinationWalletChange,
    handleAuthenticationCodeChange,
    handleAuthenticationCodeInput,
    handleConfirmationOfCustomerDistributionChange,
    handleConfirmationOfIrreversibleChange,
    handleSubmit,
    errors,
  } = useRewardsDisburseForm({ open, onDisburse, onClearRequestError })

  const {
    rewardsWalletsOptions,
    isLoading: isLoadingRewardsWallets,
    onSearchChange: setSearchRewardsWallets,
  } = useRewardsWalletsOptions({
    selectedCurrency: currency,
    selectedWalletId: destinationWalletId,
  })

  const getInput2FAError = () => {
    if (otpError) {
      return otpError
    }

    if (errors.authenticationCode?.message) {
      return errors.authenticationCode.message
    }

    const is403Error = requestError?.errorCode === 403
    if (is403Error) {
      return requestError.handledMessage as string
    }

    return undefined
  }

  return (
    <Dialog
      type="alert"
      title={t('rewards.embeddedCustomers.disburseDialog.title')}
      Illustration={<Illustration name="Warning" />}
      open={open}
      onClose={onClose}
      ActionButtons={[
        <Button key="cancel" variant="secondary" onClick={onClose} disabled={isSubmitting}>
          {t('cancel')}
        </Button>,
        <Button key="disburse" variant="primary" onClick={handleSubmit} loading={isSubmitting}>
          {t('confirm')}
        </Button>,
      ]}
    >
      <Box flexCol gapY={12}>
        <Callout variant="info">
          {t('rewards.embeddedCustomers.disburseDialog.calloutMessage')}
        </Callout>
        {requestError?.show && requestError.errorCode !== 403 && (
          <RequestErrorCallout requestError={requestError} />
        )}
        <Box flexCol>
          <DataListItem
            id="my-rewards-payout"
            title={t('rewards.embeddedCustomers.disburseDialog.myRewardsPayout')}
            body={myRewardsPayout}
          />
        </Box>
        <Box flexCol gapY={4}>
          <Combobox
            id="payout-wallet"
            label={t('rewards.embeddedCustomers.disburseDialog.payoutWalletLabel', {
              amount: myRewardsPayout,
            })}
            items={rewardsWalletsOptions}
            defaultValue={destinationWalletId}
            onChange={handleDestinationWalletChange}
            onInputChange={setSearchRewardsWallets}
            selectPlaceholder={t('selectWallet')}
            testId="payout-wallet-select"
            key={`payout-wallet-${open}`}
            errorText={errors.destinationWalletId?.message}
            loading={isLoadingRewardsWallets}
          />
        </Box>
        <Box flexCol gapY={12} className="pt-4">
          <Checkbox
            id="confirmation-1"
            name="confirmation-1"
            testId="confirmation-1"
            label={t('rewards.embeddedCustomers.disburseDialog.confirmationCustomerDistribution')}
            checked={confirmationOfCustomerDistribution}
            onCheckedChange={handleConfirmationOfCustomerDistributionChange}
            error={!!errors.confirmationOfCustomerDistribution?.message}
            errorText={errors.confirmationOfCustomerDistribution?.message}
          />
          <Checkbox
            id="confirmation-2"
            name="confirmation-2"
            testId="confirmation-2"
            label={t('rewards.embeddedCustomers.disburseDialog.confirmationOfIrreversible')}
            checked={confirmationOfIrreversible}
            onCheckedChange={handleConfirmationOfIrreversibleChange}
            error={!!errors.confirmationOfIrreversible?.message}
            errorText={errors.confirmationOfIrreversible?.message}
          />
        </Box>
        <Input2FA
          label={t('rewards.embeddedCustomers.disburseDialog.authenticationCode')}
          onChange={handleAuthenticationCodeInput}
          onComplete={handleAuthenticationCodeChange}
          key={`authentication-code-${open}`}
          error={getInput2FAError()}
        />
      </Box>
    </Dialog>
  )
}
